import { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { EventCreateEditFormValues } from "../../EventCreateEditModal/useEventCreateEditForm";
import {
  ComboboxData,
  ComboboxGroupData,
} from "apps/events/types/ComboboxData";
import { Combobox, useFilter } from "@ttc3k/trekker";

type EventKeywordsProps = {
  globalEventTags: ComboboxGroupData[];
  organizationEventTags: ComboboxData[];
};

export const useEventKeywords = ({
  globalEventTags,
  organizationEventTags,
}: EventKeywordsProps) => {
  // Save the original filtered tags
  const [filteredOrganizationTags, setFilteredOrganizationTags] = useState<
    ComboboxData[]
  >([...organizationEventTags]);

  const { control, setValue } = useFormContext<EventCreateEditFormValues>();
  const selectedGlobalTags = useWatch({
    control,
    name: "tags.global",
  });

  const selectedOrganizationTags = useWatch({
    control,
    name: "tags.organizationSpecific",
  });

  const { contains } = useFilter({ sensitivity: "base" });

  const {
    collection: filteredOrganizationTagsCollection,
    filter: filterOrganizationTags,
    set: setFilteredOrganizationTagsCollection,
  } = Combobox.useCollection({
    initialItems: organizationEventTags,
    filter: contains,
  });

  /**
   * Filter the tags based on the search input
   */
  const handleFilterOrganizationTags = (search: string) => {
    filterOrganizationTags(search);
  };

  const handleOrgTagsComboboxOpenChange = (open: boolean) => {
    if (open) {
      setFilteredOrganizationTagsCollection([...organizationEventTags]);
    }
  };

  const {
    collection: filteredGlobalTagsCollection,
    filter: filterGlobalTags,
    set: setFilteredGlobalTagsCollection,
  } = Combobox.useCollection({
    initialItems: globalEventTags
      .map(group => group.tags.map(t => ({ ...t, group: group.group })))
      .flat(),
    filter: contains,
    groupBy: item => item.group,
  });

  /**
   * Filter the tags based on the search input
   */
  const handleFilterGlobalTags = (search: string) => {
    filterGlobalTags(search);
  };

  const handleGlobalTagsComboboxOpenChange = (open: boolean) => {
    if (open) {
      setFilteredGlobalTagsCollection([
        ...globalEventTags
          .map(group => group.tags.map(t => ({ ...t, group: group.group })))
          .flat(),
      ]);
    }
  };

  // Make sure we have all of the tags in the filtered tags initially
  useEffect(() => {
    setFilteredOrganizationTags([...organizationEventTags]);
    setFilteredGlobalTagsCollection([
      ...globalEventTags
        .map(group => group.tags.map(t => ({ ...t, group: group.group })))
        .flat(),
    ]);
    setFilteredOrganizationTagsCollection([...organizationEventTags]);
  }, [
    globalEventTags,
    organizationEventTags,
    setFilteredGlobalTagsCollection,
    setFilteredOrganizationTagsCollection,
  ]);

  return {
    filteredGlobalTagsCollection,
    filteredOrganizationTags,
    filteredOrganizationTagsCollection,
    selectedGlobalTags,
    selectedOrganizationTags,
    handleFilterGlobalTags,
    handleFilterOrganizationTags,
    handleOrgTagsComboboxOpenChange,
    handleGlobalTagsComboboxOpenChange,
    setValue,
  };
};
